"use client";

import { motion } from "framer-motion";
import {
  Clock,
  Eye,
  Heart,
  MoreHorizontal,
  Play,
  Share2,
  Star,
} from "lucide-react";
import React, { useEffect } from "react";

import { useSelector } from "react-redux";
import dreamWorldProvider from "../../DreamWorldProvider";
import { selectUserGlobalData } from "../../store/dreamworldSlice";
import { LibraryItem } from "../../types/library";
import { Notification } from "../../types/notifications";

interface ContentCardProps {
  item: LibraryItem;
  onLike: (id: string, content: LibraryItem) => void;
  onClick: () => void;
  viewMode: "masonry" | "grid" | "list";
}

const ContentCard: React.FC<ContentCardProps> = ({
  item,
  onLike,
  onClick,
  viewMode,
}) => {
  const user = useSelector(selectUserGlobalData);

  useEffect(() => {
    if (item.id) {
      dreamWorldProvider.incrementViewCount(item.id);
    }
  }, [item.id]);

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    onLike(item.id, item);

    if (user && item.creator && item.creator.uid !== user.userData?.uid) {
      const notification: Omit<Notification, "id" | "timestamp"> = {
        recipientId: item.creator.uid,
        senderId: user.userData?.uid,
        type: "like",
        contentId: item.id,
        read: false,
      };
      await dreamWorldProvider.createNotification(notification);
    }
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Share functionality
    if (navigator.share) {
      navigator.share({
        title: item.title,
        text: `Check out this amazing creation by ${item.creator!.username}`,
        url: window.location.href,
      });
    }
  };

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Allow video controls to work without triggering card click
  };

  // Render media content (video or image)
  const renderMediaContent = (
    className: string,
    style?: React.CSSProperties
  ) => {
    if (item.type?.toLowerCase() === "video" && item.fullUrl) {
      return (
        <video
          src={item.fullUrl}
          className={className}
          style={style}
          controls
          preload="metadata"
          onClick={handleVideoClick}
          onMouseDown={(e) => e.stopPropagation()}
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    return (
      <img
        src={
          item.thumbnailUrl ||
          "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
        }
        alt={item.title}
        className={className}
        style={style}
      />
    );
  };

  // List view layout
  if (viewMode === "list") {
    return (
      <motion.div
        whileHover={{ y: -2 }}
        onClick={onClick}
        className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/30 hover:border-purple-500/50 transition-all cursor-pointer overflow-hidden"
      >
        <div className="flex flex-col sm:flex-row">
          {/* Media Section */}
          <div className="relative w-full sm:w-48 lg:w-56 h-48 sm:h-32 lg:h-36 flex-shrink-0">
            {renderMediaContent("w-full h-full object-cover")}

            {/* Overlay badges - only show for non-video content */}
            {item.type?.toLowerCase() !== "video" && (
              <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                {item.type?.toLowerCase() === "audio" && (
                  <div className="bg-black/60 rounded-full p-1">
                    <Play className="w-3 h-3 text-white fill-current" />
                  </div>
                )}
              </div>
            )}

            {/* Duration for videos - only show if video has no controls visible */}
            {item.duration && item.type?.toLowerCase() !== "video" && (
              <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-0.5 rounded text-xs flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>{item.duration}s</span>
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="flex-1 p-4 sm:p-3 lg:p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-white text-sm sm:text-base lg:text-lg mb-1 truncate">
                  {item.title}
                </h3>
                <div className="flex items-center space-x-2 mb-2">
                  <img
                    src={
                      item.creator!.avatarUrl ||
                      "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
                    }
                    alt={item.creator!.username}
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full"
                  />
                  <span className="text-xs sm:text-sm text-gray-300 truncate">
                    {item.creator!.username}
                  </span>
                  {true && (
                    <Star className="w-3 h-3 text-blue-400 fill-current" />
                  )}
                </div>
              </div>

              <button className="text-gray-400 hover:text-white p-1">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-1 mb-3">
              {item.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="bg-gray-700/50 text-gray-300 px-2 py-0.5 rounded text-xs"
                >
                  #{tag}
                </span>
              ))}
            </div>

            {/* Stats and Actions */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-xs sm:text-sm text-gray-400">
                <div className="flex items-center space-x-1">
                  <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{item.likes.toLocaleString()}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span>{item.views.toLocaleString()}</span>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleLike}
                  className={`p-1.5 rounded-full transition-colors ${
                    item.isLiked
                      ? "bg-red-500 text-white"
                      : "bg-gray-700/50 text-gray-400 hover:text-red-400"
                  }`}
                >
                  <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleShare}
                  className="p-1.5 rounded-full bg-gray-700/50 text-gray-400 hover:text-blue-400 transition-colors"
                >
                  <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
                </motion.button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Grid and Masonry view layout
  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.01 }}
      onClick={onClick}
      className="bg-gray-800/30 backdrop-blur-sm rounded-xl border border-gray-700/30 hover:border-purple-500/50 transition-all cursor-pointer overflow-hidden group"
      style={viewMode === "masonry" ? { height: "fit-content" } : undefined}
    >
      {/* Media Section */}
      <div className="relative">
        {renderMediaContent(
          `w-full object-cover group-hover:scale-105 transition-transform duration-300 ${
            viewMode === "grid" ? "h-48 sm:h-56 lg:h-64" : "h-auto"
          }`,
          viewMode === "masonry" && item.height
            ? { height: `${Math.min(item.height, 400)}px` }
            : undefined
        )}

        {/* Overlay badges */}
        <div className="absolute top-2 sm:top-3 left-2 sm:left-3 flex flex-wrap gap-1">
          {item.isTrending && (
            <span className="bg-orange-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">
              Trending
            </span>
          )}
        </div>

        {/* Play button overlay for videos - only show if it's not an actual video element */}
        {item.type?.toLowerCase() === "video" && !item.fullUrl && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black/60 rounded-full p-3 sm:p-4 group-hover:bg-black/80 transition-colors">
              <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white fill-current" />
            </div>
          </div>
        )}

        {/* Duration for videos - only show for non-video elements */}
        {item.duration && item.type?.toLowerCase() !== "video" && (
          <div className="absolute bottom-2 right-2 sm:bottom-3 sm:right-3 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center space-x-1">
            <Clock className="w-3 h-3" />
            <span>{item.duration}s</span>
          </div>
        )}

        {/* Action buttons overlay */}
        <div className="absolute top-2 sm:top-3 right-2 sm:right-3 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleLike}
            className={`p-2 rounded-full backdrop-blur-sm transition-colors ${
              item.isLiked
                ? "bg-red-500 text-white"
                : "bg-black/40 text-white hover:bg-red-500"
            }`}
          >
            <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleShare}
            className="p-2 rounded-full bg-black/40 text-white hover:bg-blue-500 backdrop-blur-sm transition-colors"
          >
            <Share2 className="w-3 h-3 sm:w-4 sm:h-4" />
          </motion.button>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-3 sm:p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-bold text-white text-sm sm:text-base group-hover:text-purple-400 transition-colors line-clamp-2 flex-1 mr-2">
            {item.title}
          </h3>
          <button className="text-gray-400 hover:text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <MoreHorizontal className="w-4 h-4" />
          </button>
        </div>

        {/* Creator info */}
        <div className="flex items-center space-x-2 mb-3">
          <img
            src={
              item.creator!.avatarUrl ||
              "https://plus.unsplash.com/premium_photo-1673697239981-389164b7b87f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8bmF0dXJlfGVufDB8fDB8fHww"
            }
            alt={item.creator!.username}
            className="w-5 h-5 sm:w-6 sm:h-6 rounded-full"
          />
          <span className="text-xs sm:text-sm text-gray-300 truncate flex-1">
            {item.creator!.username}
          </span>
          {true && <Star className="w-3 h-3 text-blue-400 fill-current" />}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {item.tags.slice(0, 2).map((tag) => (
            <span
              key={tag}
              className="bg-gray-700/50 text-gray-300 px-2 py-0.5 rounded text-xs"
            >
              #{tag}
            </span>
          ))}
          {item.tags.length > 2 && (
            <span className="text-gray-400 text-xs">
              +{item.tags.length - 2} more
            </span>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-xs sm:text-sm text-gray-400">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1">
              <Heart className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{item.likes.toLocaleString()}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>{item.views.toLocaleString()}</span>
            </div>
          </div>
          <span className="text-xs text-gray-500">
            {new Date(
              (item.createdAt as any).seconds * 1000
            ).toLocaleDateString()}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default ContentCard;
